// src/components/RequireAuth.js
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

export default function RequireAuth() {
  const location = useLocation();
  const [state, setState] = useState({ checking: true, ok: false, redirectTo: null });

  const getLang = () => {
    try {
      const p = new URLSearchParams(location.search || '');
      const v = p.get('lang');
      const s = v && typeof v === 'string' ? v.toLowerCase().slice(0, 2) : null;
      return s || 'de';
    } catch {
      return 'de';
    }
  };

  const setSkipPasswordFlag = (firstLogin) => {
    try {
      if (!firstLogin) sessionStorage.setItem('ONBOARDING_SKIP_PASSWORD', '1');
      else sessionStorage.removeItem('ONBOARDING_SKIP_PASSWORD');
    } catch {
      /* noop */
    }
  };

  const clearSkipPasswordFlag = () => {
    try {
      sessionStorage.removeItem('ONBOARDING_SKIP_PASSWORD');
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        // 1) Mentoring-Session prüfen (wie bisher)
        const r = await fetch('/mentoring/whoami', { credentials: 'include' });
        console.log('[RA] mentoring/whoami status:', r.status, r.ok);
        if (!r.ok) {
          console.log('[RA] mentoring/whoami NOT OK → going to /login');
          if (!cancelled) setState({ checking: false, ok: false, redirectTo: null });
          return;
        }
        const data = await r.json();
        console.log('[RA] mentoring/whoami data:', JSON.stringify({ok: data?.ok, role: data?.user?.role, onboardingComplete: data?.user?.onboarding_complete || data?.user?.onboardingComplete}));
        const ok = !!(data?.ok && data?.user?.role);
        if (!ok) {
          console.log('[RA] ok check failed:', {dataOk: data?.ok, role: data?.user?.role}, '→ going to /login');
          if (!cancelled) setState({ checking: false, ok: false, redirectTo: null });
          return;
        }

        // 2) Onboarding-Gate nur für relevante Pfade
        const path = location.pathname || '';
        const needsGate =
          path === '/mentee-dashboard' ||
          path === '/mentee-profile' ||
          path === '/onboarding-mentee' ||
          path === '/mentor-dashboard' ||
          path === '/mentor-profile' ||
          path === '/onboarding-mentor' ||
          path === '/mentoring/onboarding-mentor';

        if (!needsGate) {
          if (!cancelled) setState({ checking: false, ok: true, redirectTo: null });
          return;
        }

        // 3) Auth-WhoAmI holen (enthält onboarding_complete + firstLogin)
        let who = null;
        try {
          const r2 = await fetch('/auth/whoami', { credentials: 'include' });
          if (r2.ok) who = await r2.json();
        } catch {
          /* ignore */
        }

        const msu = data?.user || {};
        const role = String(msu?.role || '').toLowerCase();

        const onboardingComplete = Boolean(msu?.onboarding_complete ?? msu?.onboardingComplete);
        const firstLogin = Boolean(
          (who?.user?.is_first_login ?? who?.user?.first_login ?? who?.user?.firstLogin) ?? false
        );

        // Wenn Onboarding abgeschlossen ist: Flag aufräumen (sauberer Zustand)
        if (onboardingComplete) clearSkipPasswordFlag();

        // 4) Gate-Entscheidung (nur wenn NICHT bereits auf Onboarding-Seiten)
        const lang = getLang();
        const q = location.search || `?lang=${lang}`;

        console.log('[RA] gate check:', {role, onboardingComplete, firstLogin, path});

        if (role === 'mentee') {
          const profileComplete = Boolean(msu?.profile_complete ?? msu?.profileComplete);
          console.log('[RA] Mentee gate:', {onboardingComplete, profileComplete, path});

          // ✅ Wenn Onboarding schon fertig ist: Onboarding-Seite blocken
          if (onboardingComplete && path === '/onboarding-mentee') {
            clearSkipPasswordFlag();
            const dest = profileComplete ? '/mentee-dashboard' : '/mentee-profile';
            if (!cancelled) setState({ checking: false, ok: true, redirectTo: `${dest}${q}` });
            return;
          }

          // ✅ Wenn Profil NICHT vollständig ist: Dashboard sperren
          if (onboardingComplete && !profileComplete && path === '/mentee-dashboard') {
            if (!cancelled) setState({ checking: false, ok: true, redirectTo: `/mentee-profile${q}` });
            return;
          }

          // ✅ Wenn Onboarding NICHT fertig ist: alles außer Onboarding blocken
          if (!onboardingComplete && path !== '/onboarding-mentee') {
            setSkipPasswordFlag(firstLogin);
            if (!cancelled)
              setState({ checking: false, ok: true, redirectTo: `/onboarding-mentee${q}` });
            return;
          }
        }

        if (role === 'mentor') {
          // ✅ Wenn Onboarding schon fertig ist: Onboarding-Seiten blocken
          if (
            onboardingComplete &&
            (path === '/mentoring/onboarding-mentor' || path === '/onboarding-mentor')
          ) {
            clearSkipPasswordFlag();
            if (!cancelled)
              setState({ checking: false, ok: true, redirectTo: `/mentor-profile${q}` });
            return;
          }

          // ✅ Wenn Onboarding NICHT fertig ist: alles außer Onboarding blocken
          if (
            !onboardingComplete &&
            path !== '/mentoring/onboarding-mentor' &&
            path !== '/onboarding-mentor'
          ) {
            setSkipPasswordFlag(firstLogin);
            if (!cancelled)
              setState({ checking: false, ok: true, redirectTo: `/mentoring/onboarding-mentor${q}` });
            return;
          }
        }

        console.log('[RA] final: passthrough (no redirect)');
        if (!cancelled) setState({ checking: false, ok: true, redirectTo: null });
      } catch (err) {
        console.error('[RA] check() exception → going to /login:', err);
        if (!cancelled) setState({ checking: false, ok: false, redirectTo: null });
      }
    }

    setState((s) => ({ ...s, checking: true }));
    check();

    return () => {
      cancelled = true;
    };
    // Wichtig: bei Navigation (auch Browser-Back) neu prüfen
  }, [location.pathname, location.search, location.hash]);

  // Kein “Dashboard-Blitz” nach Logout/Back
  if (state.checking) return null;

  if (state.redirectTo) {
    return <Navigate to={state.redirectTo} replace state={{ from: location }} />;
  }

  if (!state.ok) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}
